// Fix: Added missing React imports
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { 
    confirmSinglePayment,
    updateChargePayments,
    saveChargesBatch, 
    updateChargeStatuses,
    updatePaymentStatusBatch,
    getBillingLockStatus,
    setBillingLockStatus,
    updateUnitGrouping,
    ungroupUnits,
    sendEmailAPI
} from '../../services';
import { calculateChargesBatch } from '../../services/feeService';
import NoticePreviewModal from '../ui/NoticePreviewModal';
import VerificationModal from '../ui/VerificationModal';
import StatementConfirmModal, { StatementUpdate } from '../ui/StatementConfirmModal';
import Spinner from '../ui/Spinner';
import { 
    SearchIcon, ChevronLeftIcon, ChevronRightIcon, 
    CheckCircleIcon, CalculatorIcon2, LockClosedIcon,
    ArrowDownTrayIcon, BanknotesIcon, ArrowUpTrayIcon, CreditCardIcon, ArrowPathIcon, MagnifyingGlassIcon,
    PaperAirplaneIcon, TrashIcon, PrinterIcon, EnvelopeIcon, ArrowUturnLeftIcon,
    ActionViewIcon, ChevronDownIcon, ChevronUpIcon, SaveIcon,
    ShareIcon, CloudArrowUpIcon, ExclamationTriangleIcon, SmartphoneIcon,
    CloudArrowDownIcon, StarIcon, PencilSquareIcon, InformationCircleIcon,
    TagIcon, XMarkIcon, LinkIcon, UnlinkIcon
} from '../ui/Icons';
import { loadScript } from '../../utils/scriptLoader';
import { formatCurrency, parseUnitCode, renderInvoiceHTMLForPdf, formatNumber } from '../../utils/helpers';
import { writeBatch, collection, query, where, getDocs, doc, addDoc, serverTimestamp, increment, updateDoc } from 'firebase/firestore';
import { db, auth } from '../../firebaseConfig';
import { isProduction } from '../../utils/env';
import type { 
    ChargeRaw, InvoiceSettings, AllData, Adjustment, 
    Role, Owner, PaymentStatus, MonthlyStat 
} from '../../types';
import { UnitType } from '../../types';
import { useNotification, useDataRefresh } from '../../App';

// --- Types & Globals ---
declare const jspdf: any;
declare const html2canvas: any;
declare const JSZip: any;
declare const XLSX: any;

// Extend ChargeRaw locally to support sentCount
type ExtendedCharge = ChargeRaw & { sentCount?: number };

// --- Components ---

const MinimalStatCard: React.FC<{ label: string; value: string; colorClass: string; onClick?: () => void }> = ({ label, value, colorClass, onClick }) => (
    <div onClick={onClick} className={`bg-white rounded-xl shadow-sm border-l-4 ${colorClass} p-4 cursor-pointer hover:shadow-md transition-shadow`}>
        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">{label}</p>
        <p className="text-2xl font-bold text-gray-800 mt-1">{value}</p>
    </div>
);

const QuickActionMenu: React.FC<{ onSelect: (method: 'paid_tm' | 'paid_ck') => void; disabled?: boolean; trigger: React.ReactNode }> = ({ onSelect, disabled, trigger }) => {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) setIsOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div className="relative" ref={menuRef}>
            <div onClick={() => !disabled && setIsOpen(!isOpen)} className={disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}>
                {trigger}
            </div>
            {isOpen && (
                <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-lg shadow-xl border border-gray-100 z-50 animate-fade-in-down overflow-hidden">
                    <button onClick={() => { onSelect('paid_tm'); setIsOpen(false); }} className="w-full text-left px-4 py-2.5 text-xs font-semibold text-gray-700 hover:bg-green-50 hover:text-green-700 flex items-center gap-2">
                        <BanknotesIcon className="w-4 h-4" /> Tiền mặt
                    </button>
                    <button onClick={() => { onSelect('paid_ck'); setIsOpen(false); }} className="w-full text-left px-4 py-2.5 text-xs font-semibold text-gray-700 hover:bg-blue-50 hover:text-blue-700 flex items-center gap-2 border-t border-gray-50">
                        <CreditCardIcon className="w-4 h-4" /> Chuyển khoản
                    </button>
                </div>
            )}
        </div>
    );
};

const MonthPickerPopover: React.FC<{
    currentPeriod: string;
    onSelectPeriod: (period: string) => void;
    onClose: () => void;
}> = ({ currentPeriod, onSelectPeriod, onClose }) => {
    const pickerRef = useRef<HTMLDivElement>(null);
    const [displayYear, setDisplayYear] = useState(new Date(currentPeriod + '-02').getFullYear());

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
                onClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    return (
        <div ref={pickerRef} className="absolute top-full mt-2 left-0 z-20 bg-white p-4 rounded-xl shadow-lg border border-gray-200 w-72">
            <div className="flex justify-between items-center mb-4">
                <button onClick={() => setDisplayYear(y => y - 1)} className="p-1 rounded-full hover:bg-gray-100"><ChevronLeftIcon /></button>
                <span className="font-bold text-lg text-gray-800">{displayYear}</span>
                <button onClick={() => setDisplayYear(y => y + 1)} className="p-1 rounded-full hover:bg-gray-100"><ChevronRightIcon /></button>
            </div>
            <div className="grid grid-cols-3 gap-2">
                {months.map((month, index) => {
                    const monthNum = String(index + 1).padStart(2, '0');
                    const value = `${displayYear}-${monthNum}`;
                    const isSelected = value === currentPeriod;
                    return (
                        <button
                            key={month}
                            onClick={() => { onSelectPeriod(value); onClose(); }}
                            className={`p-2 rounded-lg text-sm transition-colors ${isSelected ? 'bg-primary text-white font-bold' : 'hover:bg-gray-100 text-gray-700'}`}
                        >
                            {month}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

// --- Main Page ---

interface BillingPageProps {
    charges: ChargeRaw[];
    setCharges: (updater: React.SetStateAction<ChargeRaw[]>, logPayload?: any) => void;
    allData: AllData;
    onUpdateAdjustments: (updater: React.SetStateAction<Adjustment[]>, logPayload?: any) => void;
    // Fix: Removed duplicate 'role' property.
    role: Role;
    invoiceSettings: InvoiceSettings;
    onRefresh?: () => void;
}

const BillingPage: React.FC<BillingPageProps> = ({ charges, setCharges, allData, onUpdateAdjustments, role, invoiceSettings, onRefresh }) => {
    const { showToast } = useNotification();
    const { refreshData } = useDataRefresh();
    const canCalculate = ['Admin', 'Accountant'].includes(role);
    const IS_PROD = isProduction();

    // State
    const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
    const [isBillingLocked, setIsBillingLocked] = useState(false);
    
    const [isLoading, setIsLoading] = useState(false);
    const [selectedUnits, setSelectedUnits] = useState<Set<string>>(new Set());
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [floorFilter, setFloorFilter] = useState('all');
    const [previewCharge, setPreviewCharge] = useState<ChargeRaw | null>(null);
    const [editedPayments, setEditedPayments] = useState<Record<string, number>>({});
    const [manuallyUnlockedPeriods, setManuallyUnlockedPeriods] = useState<Set<string>>(new Set());
    
    // UI State
    const [showStats, setShowStats] = useState(true);
    const [isMonthPickerOpen, setIsMonthPickerOpen] = useState(false);
    const [verifyCharge, setVerifyCharge] = useState<ChargeRaw | null>(null);
    const [pendingStatementUpdates, setPendingStatementUpdates] = useState<StatementUpdate[] | null>(null);
    const [statementExpectedAmountMap, setStatementExpectedAmountMap] = useState<Map<string, number> | null>(null);
    const [isSavingStatement, setIsSavingStatement] = useState(false);
    const [groupingUnits, setGroupingUnits] = useState<string[] | null>(null);
    const [isCalculateLocked, setIsCalculateLocked] = useState(false);
    
    // Refs
    const fileInputRef = useRef<HTMLInputElement>(null);
    const lastClickTime = useRef(0);

    // Fetch Lock Status on Period Change
    useEffect(() => {
        const fetchLock = async () => {
            try {
                const status = await getBillingLockStatus(period);
                setIsBillingLocked(status);
            } catch (e) {
                console.error("Error fetching lock status", e);
            }
        };
        fetchLock();
    }, [period]);
    
    // Logic: Disable calculation for future periods
    const currentMonthStr = new Date().toISOString().slice(0, 7);
    const isFuturePeriod = period > currentMonthStr;

    // Data Filtering
    const floors = useMemo(() => {
        const nums = Array.from(new Set(allData.units.filter(u => u.UnitType === UnitType.APARTMENT).map(u => u.UnitID.slice(0, -2)))).sort((a: string, b: string) => parseInt(a,10) - parseInt(b,10));
        return [{value: 'all', label: 'Tất cả tầng'}, ...nums.map(f => ({value: f, label: `Tầng ${f}`})), {value: 'KIOS', label: 'KIOS'}];
    }, [allData.units]);

    const filteredCharges = useMemo(() => {
        return (charges as ExtendedCharge[]).filter(c => {
            if (c.Period !== period) return false;
            
            if (statusFilter !== 'all') {
                if (statusFilter === 'paid') {
                    // Include both payment methods in generic "paid" filter
                    if (!['paid', 'paid_tm', 'paid_ck'].includes(c.paymentStatus)) return false;
                } else if (statusFilter === 'debt') {
                    if (['paid', 'paid_tm', 'paid_ck'].includes(c.paymentStatus)) return false;
                } else if (statusFilter === 'pending') {
                    if (c.paymentStatus !== 'pending') return false;
                } else if (statusFilter === 'unpaid') {
                    if (c.paymentStatus !== 'unpaid') return false;
                } else {
                    // Specific status matches (paid_tm, paid_ck)
                    if (c.paymentStatus !== statusFilter) return false;
                }
            }

            if (floorFilter !== 'all') {
                const floor = c.UnitID.startsWith('K') ? 'KIOS' : parseUnitCode(c.UnitID)?.floor?.toString();
                if (floor !== floorFilter) return false;
            }
            const s = searchTerm.toLowerCase();
            if (s && !(c.UnitID.toLowerCase().includes(s) || (c.OwnerName || '').toLowerCase().includes(s))) return false;
            return true;
        }).sort((a, b) => {
            const pa = parseUnitCode(a.UnitID) || { floor: 100, apt: 0 };
            const pb = parseUnitCode(b.UnitID) || { floor: 100, apt: 0 };
            if (pa.floor !== pb.floor) return pa.floor - pb.floor;
            return pa.apt - pb.apt;
        });
    }, [charges, period, searchTerm, statusFilter, floorFilter]);

    // Stats
    const stats = useMemo(() => {
        const currentCharges = charges.filter(c => c.Period === period);
        const totalDue = currentCharges.reduce((s, c) => s + c.TotalDue, 0);
        const totalPaid = currentCharges.reduce((s, c) => s + c.TotalPaid, 0);
        const debt = totalDue - totalPaid;
        const count = currentCharges.length;
        const paidCount = currentCharges.filter(c => ['paid', 'paid_tm', 'paid_ck'].includes(c.paymentStatus)).length;
        
        return { 
            totalDue, 
            totalPaid, 
            debt, 
            progress: count > 0 ? (paidCount / count) * 100 : 0,
            count,
            paidCount
        };
    }, [charges, period]);

    const periodCharges = useMemo(() => {
        return (charges || []).filter(c => c.Period === period);
    }, [charges, period]);

    const isAllPaid = useMemo(() => {
        return periodCharges.length > 0 && periodCharges.every(c => 
            ['paid', 'paid_tm', 'paid_ck'].includes(c.paymentStatus)
        );
    }, [periodCharges]);

    const isCalculated = periodCharges.length > 0;

    useEffect(() => {
        setIsCalculateLocked(isCalculated);
    }, [isCalculated, period]);

    // Auto-lock if all paid
    useEffect(() => {
        if (isAllPaid && !isBillingLocked && canCalculate && !manuallyUnlockedPeriods.has(period)) {
            const autoLock = async () => {
                try {
                    await setBillingLockStatus(period, true);
                    setIsBillingLocked(true);
                    showToast('Tất cả căn hộ đã thanh toán. Tự động chốt sổ.', 'success');
                } catch (e) {
                    console.error("Auto-lock failed", e);
                }
            };
            autoLock();
        }
    }, [isAllPaid, isBillingLocked, period, canCalculate, showToast, manuallyUnlockedPeriods]);

    // --- Logic: Calculation ---

    const handleCalculate = async () => {
        if (isBillingLocked) {
            showToast('Kỳ này đã chốt sổ. Không thể tính toán lại.', 'error');
            return;
        }
        if (isFuturePeriod) {
            showToast('Không thể tính phí cho kỳ tương lai.', 'error');
            return;
        }

        setIsLoading(true);
        showToast('Đang tính toán lại phí...', 'info');

        try {
            const freshData = allData;
            
            // Kiểm tra cấu hình biểu phí gửi xe trước khi tính
            if (!freshData.tariffs.parking || freshData.tariffs.parking.length === 0) {
                showToast('Chưa có cấu hình biểu phí gửi xe. Vui lòng thiết lập tại trang Đơn giá.', 'error');
                setIsLoading(false);
                return;
            }

            const existingPaid = charges.filter(c => c.Period === period && ['paid', 'paid_tm', 'paid_ck', 'reconciling'].includes(c.paymentStatus));
            const paidUnitIds = new Set(existingPaid.map(c => c.UnitID));
            const unitsToCalc = freshData.units.filter(u => !paidUnitIds.has(u.UnitID));
            
            const inputs = unitsToCalc.map(unit => ({ 
                unit, 
                owner: (freshData.owners.find(o => o.OwnerID === unit.OwnerID) || { OwnerID: unit.OwnerID, OwnerName: 'Unknown', Phone: '', Email: '' }) as Owner,
                vehicles: freshData.vehicles.filter(v => v.UnitID === unit.UnitID), 
                adjustments: freshData.adjustments.filter(a => a.UnitID === unit.UnitID && a.Period === period) 
            }));

            const newCharges = await calculateChargesBatch(period, inputs, freshData);
            
            const finalNewCharges = newCharges.map(c => ({
                ...c,
                CreatedAt: new Date().toISOString(),
                Locked: false,
                paymentStatus: 'pending' as PaymentStatus,
                PaymentConfirmed: false,
                TotalPaid: c.TotalDue, // Auto-fill Input
                isPrinted: false,
                isSent: false,
                sentCount: 0 // New field
            }));

            const allChargesForPeriod = [...existingPaid, ...finalNewCharges];
            const monthlyStat: MonthlyStat = {
                period: period,
                totalService: allChargesForPeriod.reduce((sum, c) => sum + c.ServiceFee_Total, 0),
                totalParking: allChargesForPeriod.reduce((sum, c) => sum + c.ParkingFee_Total, 0),
                totalWater: allChargesForPeriod.reduce((sum, c) => sum + c.WaterFee_Total, 0),
                totalDue: allChargesForPeriod.reduce((sum, c) => sum + c.TotalDue, 0),
                updatedAt: new Date().toISOString()
            };

            await saveChargesBatch(finalNewCharges, monthlyStat);

            // Tự động chốt sổ sau khi tính phí xong
            // Remove auto-lock from calculation
            // await setBillingLockStatus(period, true);
            // setIsBillingLocked(true);

            // QUAN TRỌNG: Gọi refresh dữ liệu với force=true để xóa Cache RAM và hiện dữ liệu mới
            refreshData(true);
            showToast(`Đã tính xong phí kỳ ${period}.`, 'success');
            setIsCalculateLocked(true);

        } catch (e: any) {
            showToast(`Lỗi: ${e.message}`, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleToggleLock = async () => {
        if (!canCalculate) return;

        const now = Date.now();
        if (now - lastClickTime.current < 350) {
            if (isBillingLocked) {
                try {
                    await setBillingLockStatus(period, false);
                    setIsBillingLocked(false);
                    setManuallyUnlockedPeriods(prev => {
                        const next = new Set(prev);
                        next.add(period);
                        return next;
                    });
                    showToast('Đã mở khóa sổ. Có thể chỉnh sửa dữ liệu.', 'success');
                } catch (e) {
                    showToast('Lỗi khi mở khóa.', 'error');
                }
            } else {
                try {
                    await setBillingLockStatus(period, true);
                    setIsBillingLocked(true);
                    setManuallyUnlockedPeriods(prev => {
                        const next = new Set(prev);
                        next.delete(period);
                        return next;
                    });
                    showToast('Đã chốt sổ kỳ này.', 'success');
                } catch (e) {
                    showToast('Lỗi khi chốt sổ.', 'error');
                }
            }
        } else {
            showToast(isBillingLocked ? 'Nhấn đúp để mở khóa sổ.' : 'Nhấn đúp để chốt sổ.', 'info');
        }
        lastClickTime.current = now;
    };

    const handleStatementFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target?.result as ArrayBuffer);
                const workbook: any = XLSX.read(data, { type: 'array' });
                const firstSheetName = workbook.SheetNames[0];
                const sheet = workbook.Sheets[firstSheetName];
                const json = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][];

                let headerIndex = -1, colCredit = -1, colDesc = -1;
                
                const creditKeywords = ['credit', 'ghi co', 'ghi có', 'số tiền', 'so tien', 'phát sinh có', 'phat sinh co', 'amount', 'tiền'];
                const descKeywords = ['noi dung', 'nội dung', 'desc', 'diễn giải', 'dien giai', 'chi tiết', 'chi tiet', 'giao dịch', 'giao dich'];

                for (let i = 0; i < Math.min(20, json.length); i++) {
                    const rowArray = json[i] as any[];
                    if (!Array.isArray(rowArray)) continue;
                    const row: string[] = rowArray.map((c: any) => String(c ?? "").toLowerCase().trim());
                    
                    const foundCredit = row.findIndex(c => creditKeywords.some(kw => c.includes(kw)));
                    const foundDesc = row.findIndex(c => descKeywords.some(kw => c.includes(kw)));

                    if (foundCredit !== -1 && foundDesc !== -1 && foundCredit !== foundDesc) {
                        headerIndex = i;
                        colCredit = foundCredit;
                        colDesc = foundDesc;
                        break;
                    }
                }
                
                // Fallback: AI-like inference if headers not found
                if (headerIndex === -1) {
                    // Look for a row where one column is a number > 0 and another is a string length > 5
                    for (let i = 0; i < Math.min(20, json.length); i++) {
                        const rowArray = json[i] as any[];
                        if (!Array.isArray(rowArray) || rowArray.length < 2) continue;
                        
                        let possibleCreditCol = -1;
                        let possibleDescCol = -1;
                        
                        for (let j = 0; j < rowArray.length; j++) {
                            const val = rowArray[j];
                            if (val == null || val === '') continue;
                            
                            // Check if it looks like an amount
                            if (typeof val === 'number' && val > 0) {
                                possibleCreditCol = j;
                            } else if (typeof val === 'string') {
                                const cleanNum = parseFloat(val.replace(/[^0-9.-]+/g, ""));
                                if (!isNaN(cleanNum) && cleanNum > 0 && val.length < 20 && /\d/.test(val) && !/[a-zA-Z]/.test(val) && !val.includes('/') && !val.includes('-')) {
                                    possibleCreditCol = j;
                                } else if (val.length > 10 && /[a-zA-Z]/.test(val)) {
                                    // Check if it looks like a description
                                    possibleDescCol = j;
                                }
                            }
                        }
                        
                        if (possibleCreditCol !== -1 && possibleDescCol !== -1 && possibleCreditCol !== possibleDescCol) {
                            headerIndex = i - 1; // Assume previous row was header
                            colCredit = possibleCreditCol;
                            colDesc = possibleDescCol;
                            break;
                        }
                    }
                }

                if (colCredit === -1 || colDesc === -1) {
                    throw new Error('Không tìm thấy cột "Số tiền" và "Nội dung" trong 20 dòng đầu. Vui lòng sử dụng file mẫu.');
                }

                const validUnitsList = allData.units.map(u => u.UnitID).sort((a, b) => b.length - a.length);
                const updatesList: StatementUpdate[] = [];
                
                // Track units that already have payments in the current period
                const existingPaymentsMap = new Map<string, boolean>();
                const expectedAmountMap = new Map<string, number>();
                charges.forEach(c => {
                    if (c.Period === period) {
                        expectedAmountMap.set(c.UnitID, c.TotalDue || 0);
                        if ((c.TotalPaid || 0) > 0) {
                            existingPaymentsMap.set(c.UnitID, true);
                        }
                    }
                });

                for (let i = headerIndex + 1; i < json.length; i++) {
                    const rawAmt = json[i][colCredit];
                    let amount = 0;
                    if (typeof rawAmt === 'number') {
                        amount = Math.round(rawAmt);
                    } else if (typeof rawAmt === 'string') {
                        let cleanStr = rawAmt.trim();
                        const lastDot = cleanStr.lastIndexOf('.');
                        const lastComma = cleanStr.lastIndexOf(',');
                        if (lastDot > -1 && lastComma > -1) {
                            if (lastDot > lastComma) cleanStr = cleanStr.replace(/,/g, '');
                            else cleanStr = cleanStr.replace(/\./g, '').replace(',', '.');
                        } else if (lastComma > -1) {
                            cleanStr = cleanStr.replace(/,/g, '');
                        } else if (lastDot > -1) {
                            if (cleanStr.split('.').length > 2) cleanStr = cleanStr.replace(/\./g, '');
                            else {
                                const parts = cleanStr.split('.');
                                if (parts[1].length === 3) cleanStr = cleanStr.replace(/\./g, '');
                            }
                        }
                        amount = Math.round(parseFloat(cleanStr.replace(/[^0-9.-]+/g,"")) || 0);
                    }
                    
                    const desc = String(json[i][colDesc] || '');
                    
                    if (amount > 0) {
                        // Normalize description for matching
                        let normalizedDesc = desc.toLowerCase()
                            .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
                            .replace(/đ/g, "d").replace(/Đ/g, "D")
                            .replace(/[^a-z0-9]/g, ' ');
                            
                        // Remove common year patterns to avoid matching year as unit ID (e.g., 2026)
                        const currentYear = new Date().getFullYear();
                        const yearRegex = new RegExp(`\\b(?:20\\d{2}|${currentYear})\\b`, 'g');
                        const descWithoutYears = normalizedDesc.replace(yearRegex, ' ');
                        
                        let matchedUnit = null;
                        
                        // Strategy 1: Look for unit with explicit prefix (P, PHONG, CAN HO, etc.)
                        for (const unit of validUnitsList) {
                            const normalizedUnit = unit.toLowerCase().replace(/[^a-z0-9]/g, '');
                            const prefixRegex = new RegExp(`\\b(?:p|ph|phong|can\\s*ho|ch|apt)\\s*${normalizedUnit}\\b`, 'i');
                            if (prefixRegex.test(descWithoutYears)) {
                                matchedUnit = unit;
                                break;
                            }
                        }
                        
                        // Strategy 2: If no prefix match, look for exact unit ID match as a word
                        if (!matchedUnit) {
                            for (const unit of validUnitsList) {
                                const normalizedUnit = unit.toLowerCase().replace(/[^a-z0-9]/g, '');
                                const wordRegex = new RegExp(`\\b${normalizedUnit}\\b`, 'i');
                                if (wordRegex.test(descWithoutYears)) {
                                    matchedUnit = unit;
                                    break;
                                }
                            }
                        }
                        
                        // Strategy 3: Last resort, check original normalized desc but still avoid years
                        if (!matchedUnit) {
                            for (const unit of validUnitsList) {
                                const normalizedUnit = unit.toLowerCase().replace(/[^a-z0-9]/g, '');
                                const regex = new RegExp(`\\b${normalizedUnit}\\b`, 'i');
                                if (regex.test(descWithoutYears)) {
                                    matchedUnit = unit;
                                    break;
                                }
                            }
                        }
                        
                        if (matchedUnit) {
                            updatesList.push({
                                id: Math.random().toString(36).substr(2, 9),
                                UnitID: matchedUnit,
                                amount,
                                expectedAmount: expectedAmountMap.get(matchedUnit) || 0,
                                desc,
                                selected: true,
                                isDuplicate: existingPaymentsMap.has(matchedUnit)
                            });
                        } else {
                            // Include unmatched transactions with empty UnitID
                            updatesList.push({
                                id: Math.random().toString(36).substr(2, 9),
                                UnitID: '',
                                amount,
                                expectedAmount: 0,
                                desc,
                                selected: false, // Default to unselected if not matched
                                isDuplicate: false
                            });
                        }
                    }
                }

                if (updatesList.length > 0) {
                    setStatementExpectedAmountMap(expectedAmountMap);
                    setPendingStatementUpdates(updatesList);
                } else {
                    showToast('Không tìm thấy giao dịch nào trong file.', 'warn');
                }
            } catch (err: any) {
                showToast(err.message, 'error');
            } finally {
                if(fileInputRef.current) fileInputRef.current.value = "";
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const handleConfirmStatementUpdates = async (selectedUpdates: StatementUpdate[]) => {
        if (!selectedUpdates || selectedUpdates.length === 0) return;
        setIsSavingStatement(true);
        try {
            const updatesMap = new Map<string, number>();
            selectedUpdates.forEach(u => {
                const current = updatesMap.get(u.UnitID) || 0;
                updatesMap.set(u.UnitID, current + u.amount);
            });
            
            await updateChargePayments(period, updatesMap);
            setCharges(prev => prev.map(c => 
                (c.Period === period && updatesMap.has(c.UnitID)) 
                ? { ...c, TotalPaid: updatesMap.get(c.UnitID)!, paymentStatus: 'paid_ck', PaymentConfirmed: true } 
                : c
            ));
            showToast(`Đã đối soát ${selectedUpdates.length} giao dịch.`, 'success');
        } catch (err: any) {
            showToast(err.message, 'error');
        } finally {
            setIsSavingStatement(false);
            setPendingStatementUpdates(null);
            setStatementExpectedAmountMap(null);
        }
    };


    const handleDownloadPDFs = async () => {
        const targets = charges.filter(c => c.Period === period && selectedUnits.has(c.UnitID));
        if (targets.length === 0) return;
        setIsLoading(true);
        showToast('Đang tạo PDF...', 'info');
        try {
            await Promise.all([loadScript('jspdf'), loadScript('html2canvas'), loadScript('jszip')]);
            const { jsPDF } = jspdf;
            const JSZip = (window as any).JSZip;
            const container = document.createElement('div');
            container.style.position = 'absolute';
            container.style.left = '-9999px';
            container.style.top = '0';
            document.body.appendChild(container);
            if (targets.length === 1) {
                const charge = targets[0];
                container.innerHTML = renderInvoiceHTMLForPdf(charge, allData, invoiceSettings);
                const element = container.firstElementChild as HTMLElement;
                const canvas = await html2canvas(element, { scale: 2, useCORS: true, logging: false });
                const pdf = new jsPDF('l', 'mm', 'a5'); 
                const imgData = canvas.toDataURL('image/jpeg', 0.95);
                pdf.addImage(imgData, 'JPEG', 0, 0, 210, 148);
                pdf.save(`PhieuThu_${charge.UnitID}_${period}.pdf`);
                showToast('Đã tải xuống PDF.', 'success');
            } else {
                const zip = new JSZip();
                for (const charge of targets) {
                    container.innerHTML = renderInvoiceHTMLForPdf(charge, allData, invoiceSettings);
                    const element = container.firstElementChild as HTMLElement;
                    const canvas = await html2canvas(element, { scale: 2, useCORS: true, logging: false });
                    const pdf = new jsPDF('l', 'mm', 'a5');
                    const imgData = canvas.toDataURL('image/jpeg', 0.95);
                    pdf.addImage(imgData, 'JPEG', 0, 0, 210, 148);
                    zip.file(`PhieuThu_${charge.UnitID}_${period}.pdf`, pdf.output('blob'));
                }
                const content = await zip.generateAsync({ type: 'blob' });
                const link = document.createElement('a');
                link.href = URL.createObjectURL(content);
                link.download = `PhieuThu_${period}.zip`;
                link.click();
                showToast('Tải xuống ZIP hoàn tất.', 'success');
            }
            document.body.removeChild(container);
            setSelectedUnits(new Set());
        } catch (e) {
            showToast('Lỗi tạo PDF.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleBroadcastNotification = async (singleCharge?: ExtendedCharge) => {
        let targets: ExtendedCharge[] = [];
        if (singleCharge) targets = [singleCharge];
        else targets = (charges as ExtendedCharge[]).filter(c => c.Period === period && selectedUnits.has(c.UnitID));
        if (targets.length === 0) return;
        if (!singleCharge && !window.confirm(`Gửi thông báo phí qua App cho ${targets.length} căn hộ?`)) return;
        setIsLoading(true);
        try {
            if (IS_PROD) {
                const chunkSize = 400;
                for (let i = 0; i < targets.length; i += chunkSize) {
                    const chunk = targets.slice(i, i + chunkSize);
                    const batch = writeBatch(db);
                    chunk.forEach(c => {
                        const chargeId = `${c.Period}_${c.UnitID}`;
                        const notifRef = doc(collection(db, 'notifications'));
                        batch.set(notifRef, {
                            type: 'bill', title: `Thông báo phí T${c.Period.split('-')[1]}`,
                            body: `Tổng: ${formatCurrency(c.TotalDue)}. Vui lòng thanh toán.`,
                            userId: c.UnitID, isRead: false, createdAt: serverTimestamp(), link: 'portalBilling', chargeId: chargeId
                        });
                        const chargeRef = doc(db, 'charges', chargeId);
                        batch.update(chargeRef, { isSent: true, sentCount: increment(1) });
                    });
                    await batch.commit();
                }
            }
            refreshData(true);
            showToast(`Đã gửi thông báo cho ${targets.length} căn hộ.`, 'success');
        } catch (e: any) {
            showToast('Lỗi gửi thông báo: ' + e.message, 'error');
        } finally {
            setIsLoading(false);
            if (!singleCharge) setSelectedUnits(new Set());
        }
    };

    const handleBulkSendEmail = async (singleUnitId?: string) => {
        let targets = [];
        if (singleUnitId) targets = charges.filter(c => c.Period === period && c.UnitID === singleUnitId);
        else targets = charges.filter(c => c.Period === period && selectedUnits.has(c.UnitID));
        if (targets.length === 0) return;
        if (!invoiceSettings.appsScriptUrl) { showToast('Chưa cấu hình Email Server.', 'error'); return; }
        if (!singleUnitId && !window.confirm(`Gửi email cho ${targets.length} căn hộ?`)) return;
        setIsLoading(true);
        let successCount = 0;
        try {
            await Promise.all([loadScript('jspdf'), loadScript('html2canvas')]);
            const { jsPDF } = jspdf;
            const container = document.createElement('div');
            container.style.position = 'absolute'; container.style.left = '-9999px';
            document.body.appendChild(container);
            for (const charge of targets) {
                if (!charge.Email) continue;
                container.innerHTML = renderInvoiceHTMLForPdf(charge, allData, invoiceSettings);
                const element = container.firstElementChild as HTMLElement;
                const canvas = await html2canvas(element, { scale: 2, useCORS: true });
                const pdf = new jsPDF('l', 'mm', 'a5');
                pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', 0, 0, 210, 148);
                const pdfBlob = pdf.output('blob');
                const reader = new FileReader();
                const base64Promise = new Promise<string>((resolve) => {
                    reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
                    reader.readAsDataURL(pdfBlob);
                });
                const base64 = await base64Promise;
                const subject = (invoiceSettings.emailSubject || 'THONG BAO PHI').replace('{{period}}', period).replace('{{unit_id}}', charge.UnitID);
                const body = (invoiceSettings.emailBody || '').replace('{{owner_name}}', charge.OwnerName).replace('{{unit_id}}', charge.UnitID).replace('{{period}}', period).replace('{{total_due}}', formatCurrency(charge.TotalDue));
                const res = await sendEmailAPI(charge.Email, subject, body, invoiceSettings, base64, `PhieuThu_${charge.UnitID}.pdf`);
                if (res.success) successCount++;
            }
            document.body.removeChild(container);
            if (IS_PROD) {
                const batch = writeBatch(db);
                targets.forEach(c => { if (c.Email) batch.update(doc(db, 'charges', `${c.Period}_${c.UnitID}`), { isSent: true }); });
                await batch.commit();
            }
            refreshData(true);
            showToast(singleUnitId ? 'Đã gửi email.' : `Đã gửi thành công ${successCount}/${targets.length} email.`, 'success');
        } catch (e: any) {
            showToast('Lỗi gửi email: ' + e.message, 'error');
        } finally {
            setIsLoading(false);
            if (!singleUnitId) setSelectedUnits(new Set());
        }
    };

    const handleExportReport = () => {
        const targets = filteredCharges;
        if (targets.length === 0) { showToast('Không có dữ liệu để xuất.', 'warn'); return; }
        const header = "Kỳ,Căn hộ,Chủ hộ,Diện tích,Phí DV,SL Ô tô,SL Xe máy,SL Xe đạp,Phí Gửi xe,Tiêu thụ nước,Tiền nước,Phí khác,Điều chỉnh,Tổng phải thu,Đã nộp,Còn nợ,Trạng thái\n";
        const rows = targets.map(c => {
            const debt = c.TotalDue - c.TotalPaid;
            const escape = (val: string | number) => `"${String(val).replace(/"/g, '""')}"`;
            return [c.Period, c.UnitID, escape(c.OwnerName), c.Area_m2, c.ServiceFee_Total, c['#CAR'] + c['#CAR_A'], c['#MOTORBIKE'], c['#BICYCLE'], c.ParkingFee_Total, c.Water_m3, c.WaterFee_Total, c.OtherFees_Total || 0, c.Adjustments, c.TotalDue, c.TotalPaid, debt, c.paymentStatus].join(',');
        }).join('\n');
        const blob = new Blob(["\uFEFF" + header + rows], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url; link.setAttribute("download", `Bao_cao_phi_ky_${period}.csv`);
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
    };

    const handleDeleteBulk = async () => {
        if (isBillingLocked) return;
        if (!window.confirm("Bạn có chắc muốn xóa các dòng phí đã chọn?")) return;
        const targetIds = Array.from(selectedUnits);
        if (IS_PROD) {
            const batch = writeBatch(db);
            targetIds.forEach(id => batch.delete(doc(db, 'charges', `${period}_${id}`)));
            await batch.commit();
        }
        refreshData(true);
        setSelectedUnits(new Set());
        showToast('Đã xóa dữ liệu.', 'success');
    };

    const handleMarkPaid = async (method: 'paid_tm' | 'paid_ck' | 'pending') => {
        if (isBillingLocked) return;
        const targets = charges.filter(c => c.Period === period && selectedUnits.has(c.UnitID));
        if (targets.length === 0) return;
        setIsLoading(true);
        try {
            if (IS_PROD) {
                const batch = writeBatch(db);
                targets.forEach(c => {
                    const amount = editedPayments[c.UnitID] ?? c.TotalDue;
                    const update = { paymentStatus: method, PaymentConfirmed: method !== 'pending', TotalPaid: method === 'pending' ? 0 : amount };
                    batch.update(doc(db, 'charges', `${period}_${c.UnitID}`), update);
                });
                await batch.commit();
            }
            refreshData(true);
            showToast('Cập nhật trạng thái thành công.', 'success');
        } finally {
            setIsLoading(false);
            setSelectedUnits(new Set());
        }
    };

    const handleSinglePayment = async (charge: ChargeRaw, method: 'paid_tm' | 'paid_ck') => {
        if (isBillingLocked) return;
        if (method === 'paid_ck' && (charge.paymentStatus === 'reconciling' || charge.proofImage)) {
            setVerifyCharge(charge); return;
        }
        const isPaid = ['paid', 'paid_tm', 'paid_ck'].includes(charge.paymentStatus);
        const amount = editedPayments[charge.UnitID] ?? (isPaid ? charge.TotalPaid : charge.TotalDue);
        await confirmSinglePayment(charge, amount, method);
        refreshData(true);
        showToast(`Đã thu ${formatCurrency(amount)} cho căn ${charge.UnitID}`, 'success');
    };

    const handleVerifyConfirm = async (charge: ChargeRaw, finalAmount: number) => {
        await confirmSinglePayment(charge, finalAmount, 'paid_ck');
        refreshData(true);
        showToast(`Đã xác thực và thu ${formatCurrency(finalAmount)}`, 'success');
        setVerifyCharge(null);
    };

    const handleConfirmGrouping = async (mainUnitId: string, subUnitIds: string[]) => {
        setIsLoading(true);
        try {
            await updateUnitGrouping(mainUnitId, subUnitIds, { email: auth.currentUser?.email || 'admin', role });
            
            // Recalculate charges for affected units
            const affectedUnitIds = [mainUnitId, ...subUnitIds];
            await recalculateAffectedCharges(affectedUnitIds, (u) => {
                if (subUnitIds.includes(u.UnitID)) return { ...u, ParentUnitID: mainUnitId };
                if (u.UnitID === mainUnitId) return { ...u, ParentUnitID: undefined };
                return u;
            });

            showToast(`Đã ghép ${subUnitIds.length} căn hộ vào căn ${mainUnitId} thành công.`, 'success');
            setGroupingUnits(null);
            setSelectedUnits(new Set());
            if (onRefresh) onRefresh();
            else refreshData(true);
        } catch (e) {
            showToast('Lỗi khi ghép căn hộ.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleUngroupUnits = async () => {
        if (!window.confirm("Bạn có chắc muốn bỏ ghép các căn hộ đã chọn?")) return;
        setIsLoading(true);
        try {
            const unitIds = Array.from(selectedUnits);
            
            // Find all affected units (including parents and children of selected units)
            const affectedUnitIds = new Set<string>(unitIds);
            unitIds.forEach(id => {
                const u = allData.units.find(u => u.UnitID === id);
                if (u?.ParentUnitID) affectedUnitIds.add(u.ParentUnitID);
                allData.units.filter(sub => sub.ParentUnitID === id).forEach(sub => affectedUnitIds.add(sub.UnitID));
            });

            await ungroupUnits(unitIds, { email: auth.currentUser?.email || 'admin', role });
            
            // Recalculate charges for affected units
            await recalculateAffectedCharges(Array.from(affectedUnitIds), (u) => {
                if (unitIds.includes(u.UnitID)) return { ...u, ParentUnitID: undefined };
                return u;
            });

            showToast(`Đã bỏ ghép ${unitIds.length} căn hộ thành công.`, 'success');
            setSelectedUnits(new Set());
            if (onRefresh) onRefresh();
            else refreshData(true);
        } catch (e) {
            showToast('Lỗi khi bỏ ghép căn hộ.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const recalculateAffectedCharges = async (affectedUnitIds: string[], unitModifier: (u: any) => any) => {
        const freshData = {
            ...allData,
            units: allData.units.map(unitModifier)
        };

        const existingPaid = charges.filter(c => c.Period === period && ['paid', 'paid_tm', 'paid_ck', 'reconciling'].includes(c.paymentStatus));
        const paidUnitIds = new Set(existingPaid.map(c => c.UnitID));
        
        // Only recalculate for affected units that are not paid
        const unitsToCalc = freshData.units.filter(u => affectedUnitIds.includes(u.UnitID) && !paidUnitIds.has(u.UnitID));
        if (unitsToCalc.length === 0) return;

        const inputs = unitsToCalc.map(unit => ({ 
            unit, 
            owner: (freshData.owners.find(o => o.OwnerID === unit.OwnerID) || { OwnerID: unit.OwnerID, OwnerName: 'Unknown', Phone: '', Email: '' }) as Owner,
            vehicles: freshData.vehicles.filter(v => v.UnitID === unit.UnitID), 
            adjustments: freshData.adjustments.filter(a => a.UnitID === unit.UnitID && a.Period === period) 
        }));

        const newCharges = await calculateChargesBatch(period, inputs, freshData);
        
        const finalNewCharges = newCharges.map(c => {
            const existingCharge = charges.find(old => old.Period === period && old.UnitID === c.UnitID);
            return {
                ...c,
                CreatedAt: existingCharge?.CreatedAt || new Date().toISOString(),
                Locked: existingCharge?.Locked || false,
                paymentStatus: existingCharge?.paymentStatus || 'pending',
                PaymentConfirmed: existingCharge?.PaymentConfirmed || false,
                TotalPaid: existingCharge?.TotalPaid ?? c.TotalDue,
                isPrinted: existingCharge?.isPrinted || false,
                isSent: existingCharge?.isSent || false,
                sentCount: existingCharge?.sentCount || 0
            };
        });

        // We only save the updated charges, we don't need to recalculate the monthly stat here 
        // because it will be recalculated when refreshData is called or we can just pass undefined.
        await saveChargesBatch(finalNewCharges);
    };

    const formatPeriod = (p: string) => { const d = new Date(p + '-02'); return `T${d.getMonth() + 1}/${d.getFullYear()}`; };
    
    const handleDownloadTemplate = () => {
        const data = [
            ['Ngày giao dịch', 'Số tiền (Ghi có/Credit)', 'Nội dung (Mô tả/Description)'],
            ['2026-03-27', '1000000', 'P202 nop tien dien nuoc'],
            ['2026-03-27', '500000', 'HUD3 can 1424 phi dich vu'],
            ['2026-03-27', '250000', 'Nguyen Van A phong 1001']
        ];
        const ws = XLSX.utils.aoa_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Template');
        XLSX.writeFile(wb, 'Mau_Import_Sao_Ke.xlsx');
    };

    return (
        <div className="space-y-4 h-full flex flex-col relative">
            <input type="file" ref={fileInputRef} onChange={handleStatementFileChange} accept=".xlsx, .xls, .csv" className="hidden" />
            <div className="relative">
                <button onClick={() => setShowStats(!showStats)} className="absolute right-0 -top-8 text-gray-400 hover:text-gray-600 flex items-center gap-1 text-xs font-semibold z-10">
                    {showStats ? <><ChevronUpIcon className="w-4 h-4" /> Thu gọn</> : <><ChevronDownIcon className="w-4 h-4" /> Mở rộng</>}
                </button>
                {showStats && (
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-in-down mb-4">
                        <MinimalStatCard label="Doanh thu dự kiến" value={formatCurrency(stats.totalDue)} colorClass="border-blue-500" onClick={() => setStatusFilter('all')} />
                        <MinimalStatCard label="Thực thu" value={formatCurrency(stats.totalPaid)} colorClass="border-emerald-500" onClick={() => setStatusFilter('paid')} />
                        <MinimalStatCard label="Công nợ" value={formatCurrency(stats.debt)} colorClass="border-red-500" onClick={() => setStatusFilter('debt')} />
                        <div className="bg-white rounded-xl shadow-sm border-l-4 border-purple-500 p-4 hover:shadow-md transition-shadow">
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Tiến độ thu</p>
                            <div className="flex justify-between items-end mt-1">
                                <p className="text-2xl font-bold text-gray-800">{stats.progress.toFixed(0)}%</p>
                                <p className="text-xs text-gray-500 font-medium mb-1">{stats.paidCount}/{stats.count} căn</p>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2 mt-2 overflow-hidden">
                                <div className="bg-purple-600 h-2 rounded-full transition-all duration-500 ease-out" style={{ width: `${stats.progress}%` }}></div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="bg-white p-3 rounded-xl shadow-sm border border-gray-200 flex flex-col md:flex-row items-center gap-3">
                <div className="relative flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
                    <button onClick={() => setPeriod(p => { const d=new Date(p+'-02'); d.setMonth(d.getMonth()-1); return d.toISOString().slice(0,7); })} className="p-1.5 hover:bg-gray-200 rounded"><ChevronLeftIcon /></button>
                    <button onClick={() => setIsMonthPickerOpen(!isMonthPickerOpen)} className="px-3 font-bold text-gray-800 text-sm w-24 text-center hover:bg-gray-200 rounded py-1.5">{formatPeriod(period)}</button>
                    {isMonthPickerOpen && <MonthPickerPopover currentPeriod={period} onSelectPeriod={setPeriod} onClose={() => setIsMonthPickerOpen(false)} />}
                    <button onClick={() => setPeriod(p => { const d=new Date(p+'-02'); d.setMonth(d.getMonth()+1); return d.toISOString().slice(0,7); })} className="p-1.5 hover:bg-gray-200 rounded"><ChevronRightIcon /></button>
                </div>
                <div className="relative flex-grow min-w-[200px]">
                    <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input type="text" placeholder="Tìm căn hộ..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full h-9 pl-9 pr-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary outline-none bg-white text-gray-900" />
                </div>
                <select value={floorFilter} onChange={e => setFloorFilter(e.target.value)} className="h-9 px-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 outline-none">{floors.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}</select>
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="h-9 px-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 outline-none">
                    <option value="all">Tất cả trạng thái</option>
                    <option value="pending">Đang chờ</option>
                    <option value="paid">Đã thu (Tất cả)</option>
                    <option value="paid_tm">Đã thu (TM)</option>
                    <option value="paid_ck">Đã thu (CK)</option>
                    <option value="debt">Còn nợ</option>
                </select>
                <div className="flex items-center gap-2 border-l pl-3">
                    {onRefresh && <button onClick={onRefresh} className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-600 border border-transparent hover:border-gray-200" title="Làm mới dữ liệu"><ArrowPathIcon className="w-5 h-5" /></button>}
                    <button 
                        onClick={() => {
                            if (isBillingLocked) {
                                showToast('Kỳ này đã chốt sổ. Không thể tính toán lại.', 'error');
                                return;
                            }
                            if (isCalculateLocked) {
                                const now = Date.now();
                                if (now - lastClickTime.current < 350) {
                                    setIsCalculateLocked(false);
                                    showToast('Đã mở khóa nút tính phí.', 'success');
                                } else {
                                    showToast('Nhấn đúp để mở khóa nút tính phí.', 'info');
                                }
                                lastClickTime.current = now;
                            } else {
                                handleCalculate();
                            }
                        }} 
                        disabled={isLoading || !canCalculate || (isFuturePeriod && !isBillingLocked) || isBillingLocked} 
                        title={isBillingLocked ? "Đã khóa sổ" : isCalculateLocked ? "Đã tính phí - Nhấn đúp để mở khóa" : "Tính phí"} 
                        className={`h-9 px-4 rounded-lg font-bold text-sm flex items-center gap-2 text-white shadow-sm transition-colors ${(isBillingLocked || isCalculateLocked) ? 'bg-gray-400' : isFuturePeriod ? 'bg-gray-300 cursor-not-allowed' : 'bg-[#006f3a] hover:bg-[#005a2f]'}`}
                    >
                        {isLoading ? <Spinner /> : (isBillingLocked || isCalculateLocked) ? <LockClosedIcon className="w-4 h-4"/> : <CalculatorIcon2 className="w-4 h-4"/>}
                        {(isBillingLocked || isCalculateLocked) ? "Đã tính" : "Tính phí"}
                    </button>
                    <div className="relative group">
                        <button onClick={() => fileInputRef.current?.click()} disabled={isBillingLocked} title="Nhập sao kê để đối soát" className={`h-9 px-3 border border-gray-300 font-semibold rounded-lg flex items-center gap-1 text-sm ${isBillingLocked ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}><ArrowUpTrayIcon className="w-4 h-4"/> Import</button>
                        <button onClick={handleDownloadTemplate} className="absolute -top-10 left-1/2 -translate-x-1/2 hidden group-hover:flex whitespace-nowrap bg-gray-800 text-white text-xs px-2 py-1 rounded shadow-lg items-center gap-1 z-50">Tải file mẫu</button>
                    </div>
                    <button onClick={handleExportReport} className="h-9 px-3 border border-gray-300 font-semibold rounded-lg flex items-center gap-1 text-sm bg-white text-gray-700 hover:bg-gray-50" title="Xuất báo cáo chi tiết (CSV)"><ArrowDownTrayIcon className="w-4 h-4"/> Export</button>
                    <div className="ml-2 border-l pl-3">
                        <button 
                            onClick={handleToggleLock} 
                            disabled={!canCalculate} 
                            className={`h-9 px-4 rounded-lg font-bold text-sm flex items-center gap-2 shadow-sm transition-colors ${isBillingLocked ? 'bg-gray-600 text-white hover:bg-gray-700 border-gray-600' : 'bg-blue-600 text-white hover:bg-blue-700 border-blue-600'}`} 
                            title={isBillingLocked ? "Đã chốt sổ. Nhấn đúp để mở khóa" : "Chốt sổ (Khóa dữ liệu)"}
                        >
                            {isBillingLocked ? <LockClosedIcon className="w-4 h-4" /> : <SaveIcon className="w-4 h-4" />}
                            {isBillingLocked ? "Đã chốt" : "Chốt sổ"}
                        </button>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex-1 flex flex-col overflow-hidden relative">
                <div className="overflow-y-auto">
                    <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 sticky top-0 z-10 border-b border-gray-200">
                            <tr>
                                <th className="w-10 px-4 py-3 text-center"><input type="checkbox" disabled={isBillingLocked} checked={selectedUnits.size > 0 && selectedUnits.size === filteredCharges.length} onChange={() => setSelectedUnits(prev => prev.size === filteredCharges.length ? new Set() : new Set(filteredCharges.map(c => c.UnitID)))} className="w-4 h-4 rounded text-primary focus:ring-primary cursor-pointer disabled:opacity-50"/></th>
                                <th className="px-4 py-3 text-left font-bold text-gray-600">Căn hộ</th>
                                <th className="px-4 py-3 text-left font-bold text-gray-600">Chủ hộ</th>
                                <th className="px-4 py-3 text-right font-bold text-gray-600">Tổng phí</th>
                                <th className="px-4 py-3 text-right font-bold text-gray-600 w-32">Thực thu</th>
                                <th className="px-4 py-3 text-right font-bold text-gray-600">C.Lệch</th>
                                <th className="px-4 py-3 text-center font-bold text-gray-600">Trạng thái</th>
                                <th className="px-4 py-3 text-center font-bold text-gray-600 w-32">H.Động</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {filteredCharges.length === 0 ? (
                                <tr><td colSpan={8} className="p-8 text-center text-gray-500">Chưa có dữ liệu.</td></tr>
                            ) : (
                                (filteredCharges as ExtendedCharge[]).map(charge => {
                                    const finalPaid = editedPayments[charge.UnitID] ?? charge.TotalPaid;
                                    const diff = finalPaid - charge.TotalDue;
                                    const isPaid = ['paid', 'paid_tm', 'paid_ck'].includes(charge.paymentStatus);
                                    let statusBadge;
                                    if (charge.paymentStatus === 'paid_tm') {
                                        statusBadge = <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-green-100 text-green-800 border border-green-200">Đã thu (TM)</span>;
                                    } else if (charge.paymentStatus === 'paid_ck' || charge.paymentStatus === 'paid') {
                                        statusBadge = <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-blue-100 text-blue-800 border border-blue-200">Đã thu (CK)</span>;
                                    } else if (charge.paymentStatus === 'reconciling') {
                                        statusBadge = <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-pink-100 text-pink-800 border border-pink-200">Chờ đối soát</span>;
                                    } else if (charge.sentCount && charge.sentCount > 0) {
                                        const color = charge.sentCount > 1 ? 'text-blue-800 bg-blue-100 border-blue-200' : 'text-cyan-800 bg-cyan-100 border-cyan-200';
                                        statusBadge = <span className={`px-2 py-0.5 text-xs font-bold rounded-full border ${color}`}>Đã gửi - {charge.sentCount}</span>;
                                    } else {
                                        statusBadge = <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-orange-100 text-orange-800 border border-orange-200">Đang chờ</span>;
                                    }

                                    return (
                                        <tr key={charge.UnitID} className={`hover:bg-gray-50 transition-colors ${selectedUnits.has(charge.UnitID) ? 'bg-blue-50' : ''} ${isBillingLocked ? 'opacity-95' : ''}`}>
                                            <td className="px-4 py-3 text-center"><input type="checkbox" disabled={isBillingLocked} checked={selectedUnits.has(charge.UnitID)} onChange={() => setSelectedUnits(p => { const n = new Set(p); const id = charge.UnitID; if(n.has(id)) n.delete(id); else n.add(id); return n; })} className="w-4 h-4 rounded text-primary focus:ring-primary cursor-pointer disabled:opacity-50"/></td>
                                            <td className="px-4 py-3 font-bold text-gray-900">
                                                <div className="flex items-center gap-2">
                                                    {!isBillingLocked && !isPaid && (
                                                        <button 
                                                            onClick={() => recalculateAffectedCharges([charge.UnitID], u => u)}
                                                            className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-emerald-600 transition-colors"
                                                            title="Tính lại phí riêng cho căn này"
                                                        >
                                                            <CalculatorIcon2 className="w-3.5 h-3.5" />
                                                        </button>
                                                    )}
                                                    <span className="text-sm font-bold text-gray-900">{charge.UnitID}</span>
                                                    {charge.ParentUnitID && (
                                                        <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 text-[10px] font-bold rounded flex items-center gap-1" title={`Gộp về căn ${charge.ParentUnitID}`}>
                                                            <ShareIcon className="w-2.5 h-2.5" /> Phụ
                                                        </span>
                                                    )}
                                                    {charge.SubUnits && charge.SubUnits.length > 0 && (
                                                        <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-bold rounded flex items-center gap-1" title={`Gộp phí từ ${charge.SubUnits.join(', ')}`}>
                                                            <TagIcon className="w-2.5 h-2.5" /> Chính ({charge.SubUnits.length})
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-gray-700 flex items-center gap-2">{charge.paymentStatus === 'reconciling' && <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" title="Có biên lai cần duyệt"></div>}{charge.OwnerName}</td>
                                            <td className="px-4 py-3 text-right font-medium text-gray-900">{formatNumber(charge.TotalDue)}</td>
                                            <td className="px-4 py-3 text-right">
                                                {charge.ParentUnitID ? (
                                                    <span className="text-xs font-bold text-gray-400 italic">Đã gộp về {charge.ParentUnitID}</span>
                                                ) : (
                                                    <input 
                                                        type="text" 
                                                        value={formatNumber(isPaid ? finalPaid : (editedPayments[charge.UnitID] ?? charge.TotalDue))} 
                                                        onChange={e => { 
                                                            if (!isPaid && !isBillingLocked) {
                                                                const val = parseInt(e.target.value.replace(/\D/g, '') || '0', 10); 
                                                                setEditedPayments(prev => ({ ...prev, [charge.UnitID]: val })); 
                                                            }
                                                        }}
                                                        readOnly={isPaid || isBillingLocked}
                                                        className={isPaid 
                                                            ? "w-full text-right p-1.5 text-sm border border-green-200 rounded-md bg-green-50 text-green-700 font-bold focus:outline-none" 
                                                            : "w-full text-right p-1.5 text-sm border border-gray-300 rounded-md bg-white text-gray-900 focus:ring-2 focus:ring-[#006f3a] focus:border-transparent outline-none"}
                                                    />
                                                )}
                                            </td>
                                            <td className={`px-4 py-3 text-right font-bold ${diff === 0 ? 'text-gray-300' : diff > 0 ? 'text-blue-600' : 'text-red-500'}`}>{diff === 0 ? '-' : formatNumber(diff)}</td>
                                            <td className="px-4 py-3 text-center">{statusBadge}</td>
                                            <td className="px-4 py-3 text-center">
                                                <div className="flex justify-center gap-1">
                                                    <button onClick={() => setPreviewCharge(charge)} title="Xem chi tiết" className="p-1.5 rounded hover:bg-gray-200 text-gray-500 hover:text-gray-700"><ActionViewIcon className="w-4 h-4"/></button>
                                                    <button onClick={() => handleBroadcastNotification(charge)} disabled={isBillingLocked} title="Gửi App Notification" className={`p-1.5 rounded hover:bg-blue-50 text-blue-500 hover:text-blue-700 ${isBillingLocked ? 'opacity-50 cursor-not-allowed' : ''}`}><PaperAirplaneIcon className="w-4 h-4"/></button>
                                                    <QuickActionMenu onSelect={(m) => handleSinglePayment(charge, m)} disabled={role === 'Operator' || isBillingLocked} trigger={<button title="Xác nhận thu" className={`p-1.5 rounded hover:bg-green-50 text-green-500 hover:text-green-700 ${isBillingLocked ? 'opacity-50 cursor-not-allowed' : ''}`}>{charge.paymentStatus === 'reconciling' ? <MagnifyingGlassIcon className="w-4 h-4 text-purple-600"/> : <CheckCircleIcon className="w-4 h-4"/>}</button>}/>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {selectedUnits.size > 0 && (() => {
                const selectedUnitsList = Array.from(selectedUnits);
                const isAnyGrouped = selectedUnitsList.some(id => {
                    const u = allData.units.find(u => u.UnitID === id);
                    return u?.ParentUnitID || allData.units.some(sub => sub.ParentUnitID === id);
                });

                return (
                    <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-white rounded-full shadow-2xl border border-gray-200 p-2 flex items-center gap-4 z-50 animate-fade-in-down">
                        <div className="pl-4 pr-3 border-r border-gray-200 flex items-center gap-2"><span className="text-sm font-bold text-gray-800">{selectedUnits.size}</span><button onClick={() => setSelectedUnits(new Set())} className="text-xs text-red-500 hover:underline">Bỏ chọn</button></div>
                        <div className="flex items-center gap-2">
                            {isAnyGrouped ? (
                                <button 
                                    onClick={handleUngroupUnits}
                                    className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-rose-50 text-rose-700 hover:bg-rose-100 text-xs font-bold"
                                >
                                    <UnlinkIcon className="w-4 h-4" /> Bỏ ghép căn
                                </button>
                            ) : selectedUnits.size >= 2 ? (
                                <button 
                                    onClick={() => setGroupingUnits(Array.from(selectedUnits))}
                                    className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-xs font-bold"
                                >
                                    <LinkIcon className="w-4 h-4" /> Ghép căn
                                </button>
                            ) : null}
                            <button onClick={() => handleMarkPaid('paid_tm')} disabled={isLoading || isBillingLocked} className={`flex items-center gap-1 px-3 py-1.5 rounded-full bg-green-50 text-green-700 hover:bg-green-100 text-xs font-bold ${isBillingLocked ? 'opacity-50 cursor-not-allowed' : ''}`}><BanknotesIcon className="w-4 h-4"/> Thu TM</button>
                            <button onClick={() => handleMarkPaid('paid_ck')} disabled={isLoading || isBillingLocked} className={`flex items-center gap-1 px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100 text-xs font-bold ${isBillingLocked ? 'opacity-50 cursor-not-allowed' : ''}`}><CreditCardIcon className="w-4 h-4"/> Thu CK</button>
                            <button onClick={() => handleMarkPaid('pending')} disabled={isLoading || isBillingLocked} className={`flex items-center gap-1 px-3 py-1.5 rounded-full bg-orange-50 text-orange-700 hover:bg-orange-100 text-xs font-bold ${isBillingLocked ? 'opacity-50 cursor-not-allowed' : ''}`}><ArrowUturnLeftIcon className="w-4 h-4"/> Hủy thu</button>
                        </div>
                        <div className="flex items-center gap-2 border-l pl-3 border-gray-200">
                            <button onClick={() => handleBroadcastNotification()} disabled={isLoading || isBillingLocked} className={`p-2 rounded-full hover:bg-cyan-50 text-cyan-600 ${isBillingLocked ? 'opacity-50 cursor-not-allowed' : ''}`} title="Gửi App"><PaperAirplaneIcon className="w-5 h-5"/></button>
                            <button onClick={() => handleBulkSendEmail()} disabled={isLoading || isBillingLocked} className={`p-2 rounded-full hover:bg-indigo-50 text-indigo-600 ${isBillingLocked ? 'opacity-50 cursor-not-allowed' : ''}`} title="Gửi Email"><EnvelopeIcon className="w-5 h-5"/></button>
                            <button onClick={handleDownloadPDFs} disabled={isLoading} className="p-2 rounded-full hover:bg-gray-100 text-gray-700" title="Tải PDF"><PrinterIcon className="w-5 h-5"/></button>
                        </div>
                        <div className="pl-2 border-l border-gray-200 pr-2"><button onClick={handleDeleteBulk} disabled={isLoading || isBillingLocked} className={`p-2 rounded-full hover:bg-red-50 text-red-600 ${isBillingLocked ? 'opacity-50 cursor-not-allowed' : ''}`} title="Xóa"><TrashIcon className="w-5 h-5"/></button></div>
                    </div>
                );
            })()}
            {previewCharge && <NoticePreviewModal charge={previewCharge} onClose={() => setPreviewCharge(null)} invoiceSettings={invoiceSettings} allData={allData} onSendEmail={() => handleBulkSendEmail(previewCharge.UnitID)}/>}
            {verifyCharge && <VerificationModal charge={verifyCharge} onClose={() => setVerifyCharge(null)} onConfirm={handleVerifyConfirm}/>}
            
            {pendingStatementUpdates && (
                <StatementConfirmModal
                    updates={pendingStatementUpdates}
                    allUnits={allData.units}
                    expectedAmountMap={statementExpectedAmountMap || undefined}
                    onConfirm={handleConfirmStatementUpdates}
                    onCancel={() => {
                        setPendingStatementUpdates(null);
                        setStatementExpectedAmountMap(null);
                    }}
                    isSaving={isSavingStatement}
                />
            )}

            {groupingUnits && (
                <UnitGroupingModal 
                    selectedUnits={groupingUnits}
                    allUnits={allData.units}
                    onConfirm={handleConfirmGrouping}
                    onClose={() => setGroupingUnits(null)}
                />
            )}
        </div>
    );
};

const UnitGroupingModal: React.FC<{
    selectedUnits: string[];
    allUnits: any[];
    onConfirm: (mainUnitId: string, subUnitIds: string[]) => void;
    onClose: () => void;
}> = ({ selectedUnits, allUnits, onConfirm, onClose }) => {
    const [mainUnitId, setMainUnitId] = useState(selectedUnits[0]);
    const units = allUnits.filter(u => selectedUnits.includes(u.UnitID));

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
                <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gradient-to-r from-primary/5 to-transparent">
                    <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                        <ShareIcon className="text-primary" /> Ghép căn hộ
                    </h3>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                        <XMarkIcon className="w-5 h-5 text-gray-400" />
                    </button>
                </div>
                <div className="p-6 space-y-4">
                    <p className="text-sm text-gray-600 leading-relaxed">
                        Bạn đang chọn ghép <span className="font-bold text-primary">{selectedUnits.length}</span> căn hộ. 
                        Tất cả phí sẽ được gộp về căn hộ <span className="font-bold text-gray-800">Chính</span>. 
                        Các căn hộ <span className="font-bold text-gray-800">Phụ</span> sẽ có phí bằng 0.
                    </p>
                    
                    <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Chọn căn hộ chính</label>
                        <div className="grid grid-cols-1 gap-2">
                            {units.map(u => (
                                <label 
                                    key={u.UnitID} 
                                    className={`flex items-center justify-between p-3 rounded-xl border-2 cursor-pointer transition-all ${
                                        mainUnitId === u.UnitID 
                                            ? 'border-primary bg-primary/5 ring-1 ring-primary/20' 
                                            : 'border-gray-100 hover:border-gray-200 bg-gray-50/50'
                                    }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${mainUnitId === u.UnitID ? 'border-primary bg-primary' : 'border-gray-300'}`}>
                                            {mainUnitId === u.UnitID && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                                        </div>
                                        <div>
                                            <p className="font-bold text-gray-800">{u.UnitID}</p>
                                            <p className="text-[10px] text-gray-500">{u.OwnerName}</p>
                                        </div>
                                    </div>
                                    <input 
                                        type="radio" 
                                        name="mainUnit" 
                                        className="hidden" 
                                        checked={mainUnitId === u.UnitID} 
                                        onChange={() => setMainUnitId(u.UnitID)} 
                                    />
                                    {mainUnitId === u.UnitID ? (
                                        <span className="text-[10px] font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full uppercase tracking-tighter">Chính</span>
                                    ) : (
                                        <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full uppercase tracking-tighter">Phụ</span>
                                    )}
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="p-6 bg-gray-50 flex gap-3">
                    <button onClick={onClose} className="flex-1 py-3 text-sm font-bold text-gray-500 hover:bg-gray-100 rounded-xl transition-colors">Hủy</button>
                    <button 
                        onClick={() => onConfirm(mainUnitId, selectedUnits.filter(id => id !== mainUnitId))}
                        className="flex-1 py-3 text-sm font-bold text-white bg-primary hover:bg-primary-dark rounded-xl shadow-lg shadow-primary/20 transition-all active:scale-95"
                    >
                        Xác nhận ghép
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BillingPage;
